import React, { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useCompetitors } from '@/hooks/useCompetitors';
import { useInstagramProfiles } from '@/hooks/useInstagramProfiles';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import CompetitorComparisonCard from './CompetitorComparisonCard';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { TrendingUp, Info, ArrowRight, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  getBaselineDate, 
  getDisplayStartDate,
  calculatePeriodChanges,
  calculateGrowthPercent,
  TimeRange 
} from '@/utils/periodCalculations';

interface ProfileMetrics {
  username: string;
  followers: number;
  postsCount: number;
  postsPerWeek: number | null;
  growth: number | null;
  followersChange: number | null;
  isCurrentUser: boolean;
}

interface CompetitorComparisonProps {
  showAll?: boolean;
  timeRange?: TimeRange;
}

export const CompetitorComparison = ({ showAll = false, timeRange = 'week' }: CompetitorComparisonProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { profiles, isLoading: profilesLoading } = useInstagramProfiles(user?.id || null);
  const { competitors, isLoading: competitorsLoading } = useCompetitors();
  const [allMetrics, setAllMetrics] = useState<ProfileMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [correlation, setCorrelation] = useState<string | null>(null);

  // Wait until dependency hooks have finished their initial load
  const depsReady = !profilesLoading && !competitorsLoading;

  useEffect(() => {
    if (!depsReady) return;

    const fetchMetrics = async () => {
      if (!user) return;

      // Only show skeleton on first load, not on subsequent re-fetches
      if (allMetrics.length === 0) {
        setIsLoading(true);
      }
      const metrics: ProfileMetrics[] = [];
      
      // Use shared utility for date calculations
      const baselineDate = getBaselineDate(timeRange);
      const displayStart = getDisplayStartDate(timeRange);
      
      // Calculate cutoff date for query (go back a bit more for safety)
      const cutoffDate = new Date(baselineDate);
      cutoffDate.setDate(cutoffDate.getDate() - 5);

      try {
        // Fetch current user metrics
        if (profiles.length > 0) {
          const userProfile = profiles[0];
          
          const { data: rawUserMetrics } = await supabase
            .from('instagram_daily_metrics')
            .select('*')
            .ilike('username', userProfile.username)
            .order('scrape_date', { ascending: false });

          // Deduplicate by scrape_date (keep first = most recent entry per day)
          const uniqueByDate = new Map<string, NonNullable<typeof rawUserMetrics>[0]>();
          rawUserMetrics?.forEach(m => {
            if (!uniqueByDate.has(m.scrape_date)) {
              uniqueByDate.set(m.scrape_date, m);
            }
          });
          const userMetrics = Array.from(uniqueByDate.values()).slice(0, 45);

          if (userMetrics.length > 0) {
            const recent = userMetrics[0];

            // Transform to MetricDataPoint format for shared utilities
            const dataPoints = userMetrics.map(m => ({
              date: m.scrape_date,
              followers: m.followers_count,
              following: m.following_count,
              posts: m.posts_count,
            }));
            
            // Use shared calculation utilities
            const periodChanges = calculatePeriodChanges(dataPoints, timeRange);
            const growth = calculateGrowthPercent(dataPoints, timeRange);
            
            // Calculate posts in period
            let postsPerWeek: number | null = null;
            if (periodChanges) {
              postsPerWeek = periodChanges.posts;
            }

            // Always add user metrics even with only 1 data point - show current values
            metrics.push({
              username: userProfile.username,
              followers: recent.followers_count,
              postsCount: recent.posts_count,
              postsPerWeek,
              growth,
              followersChange: periodChanges?.followers ?? null,
              isCurrentUser: true
            });
          } else {
            // No metrics yet - add placeholder for user
            metrics.push({
              username: userProfile.username,
              followers: 0,
              postsCount: 0,
              postsPerWeek: null,
              growth: null,
              followersChange: null,
              isCurrentUser: true
            });
          }
        }

        // Fetch competitor metrics
        const sourceCompetitors = showAll ? competitors : competitors.filter(c => c.is_active);
        
        for (const competitor of sourceCompetitors) {
          const { data: compMetrics } = await supabase
            .from('competitor_daily_metrics')
            .select('*')
            .eq('username', competitor.instagram_username)
            .eq('user_id', user.id)
            .gte('metric_date', cutoffDate.toISOString().split('T')[0])
            .order('metric_date', { ascending: false });


          if (compMetrics && compMetrics.length > 0) {
            const recent = compMetrics[0];
            
            // Transform to MetricDataPoint format for shared utilities
            const dataPoints = compMetrics.map(m => ({
              date: m.metric_date,
              followers: m.followers_count || 0,
              following: m.following_count || 0,
              posts: m.posts_count || 0,
            }));
            
            // Use shared calculation utilities
            const periodChanges = calculatePeriodChanges(dataPoints, timeRange);
            const growth = calculateGrowthPercent(dataPoints, timeRange);
            
            // Calculate posts in period
            let postsPerWeek: number | null = null;
            if (periodChanges) {
              postsPerWeek = periodChanges.posts;
            }

            metrics.push({
              username: competitor.instagram_username,
              followers: recent.followers_count || 0,
              postsCount: recent.posts_count || 0,
              postsPerWeek,
              growth,
              followersChange: periodChanges?.followers ?? null,
              isCurrentUser: false
            });
          } else {
            // Add competitor even without metrics to show "collecting data" state
            metrics.push({
              username: competitor.instagram_username,
              followers: 0,
              postsCount: 0,
              postsPerWeek: null,
              growth: null,
              followersChange: null,
              isCurrentUser: false
            });
          }
        }

        // Calculate correlation insight
        const validData = metrics.filter(m => m.postsPerWeek !== null && m.growth !== null);
        if (validData.length >= 2) {
          const avgPostsPerWeek = validData.reduce((sum, m) => sum + (m.postsPerWeek || 0), 0) / validData.length;
          const avgGrowth = validData.reduce((sum, m) => sum + (m.growth || 0), 0) / validData.length;
          
          if (avgGrowth > 0 && avgPostsPerWeek > 3) {
            setCorrelation(`More posts correlate with higher growth. Aim for 4-5 posts/week`);
          } else if (avgPostsPerWeek < 3) {
            setCorrelation(`Post more frequently to boost growth. Target: 4-5 posts/week`);
          }
        }

        setAllMetrics(metrics);
      } catch (error) {
        console.error('Error fetching comparison metrics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetrics();
  }, [depsReady, user, profiles, competitors, showAll, timeRange]);

  if (isLoading) {
    return (
      <div className="glass-card rounded-2xl p-8 border border-border/50">
        <div className="h-24 bg-muted/40 rounded-lg animate-pulse" />
      </div>
    );
  }

  // Check if we're waiting for data (have profile but metrics show no comparison data yet)
  // This happens when there's only one data point - no historical data to compare
  const hasProfileButNoData = profiles.length > 0 && allMetrics.length > 0 && allMetrics.every(m => 
    m.growth === null && m.followersChange === null && m.postsPerWeek === null
  );
  const showPlaceholder = !showAll && hasProfileButNoData;

  // Placeholder data for loading state - 3 items to match FeaturedContentIdeas
  const placeholderMetrics: ProfileMetrics[] = [
    { username: profiles[0]?.username || '@twoj_profil', followers: 0, postsCount: 0, postsPerWeek: null, growth: null, followersChange: null, isCurrentUser: true },
    { username: '@konkurent_1', followers: 0, postsCount: 0, postsPerWeek: null, growth: null, followersChange: null, isCurrentUser: false },
    { username: '@konkurent_2', followers: 0, postsCount: 0, postsPerWeek: null, growth: null, followersChange: null, isCurrentUser: false },
  ];

  // Check if user has no competitors (only user metric exists)
  const hasNoCompetitors = allMetrics.length <= 1 && allMetrics.every(m => m.isCurrentUser);

  if (allMetrics.length === 0 && !showPlaceholder) {
    // On Competitors page, show helpful empty state; on Dashboard show CTA
    if (showAll) {
      return (
        <div className="glass-card rounded-2xl p-8 md:p-12 h-full flex flex-col items-center justify-center text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
          <div className="relative z-10 space-y-4 max-w-md">
            <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">{t('competitors.noCompetitors')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t('competitors.noCompetitorsDesc')}</p>
          </div>
        </div>
      );
    }
    // Dashboard - show CTA card
    return (
      <div className="glass-card rounded-2xl p-6 md:p-8 h-full flex flex-col relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
        
        {/* Header */}
        <div className="relative z-10 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              {t('competitors.performanceComparison')}
            </h2>
          </div>
          <p className="text-sm text-muted-foreground ml-13">
            {t('competitors.youVsBest')}
          </p>
        </div>

        {/* CTA Content */}
        <div className="flex-1 relative z-10 flex flex-col items-center justify-center text-center py-8">
          <div className="w-14 h-14 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <TrendingUp className="w-7 h-7 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Dodaj konkurencję
          </h3>
          <p className="text-sm text-muted-foreground mb-5 max-w-[280px]">
            Sprawdź jak wypadasz na tle innych twórców w Twojej branży
          </p>
          <Button
            onClick={() => navigate('/onboarding/dodaj-konkurencje')}
            className="shadow-md"
          >
            Dodaj profile
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Dashboard - show blurred placeholder when only user exists (no competitors added)
  if (!showAll && hasNoCompetitors && !showPlaceholder) {
    // Create fake placeholder metrics that look realistic
    const fakePlaceholderMetrics: ProfileMetrics[] = [
      { 
        username: profiles[0]?.username || '@twoj_profil', 
        followers: 12500, 
        postsCount: 245, 
        postsPerWeek: 2, 
        growth: 0.45, 
        followersChange: 56, 
        isCurrentUser: true 
      },
      { 
        username: '@competitor_1', 
        followers: 28400, 
        postsCount: 512, 
        postsPerWeek: 4, 
        growth: 0.82, 
        followersChange: 231, 
        isCurrentUser: false 
      },
      { 
        username: '@competitor_2', 
        followers: 8900, 
        postsCount: 178, 
        postsPerWeek: 3, 
        growth: 0.31, 
        followersChange: 28, 
        isCurrentUser: false 
      },
    ];
    
    
    return (
      <div className="glass-card rounded-2xl p-6 md:p-8 h-full flex flex-col relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
        
        {/* Header - always visible */}
        <div className="relative z-20 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              {t('competitors.performanceComparison')}
            </h2>
          </div>
          <p className="text-sm text-muted-foreground ml-13">
            {t('competitors.youVsBest')}
          </p>
        </div>

        {/* Blurred content with overlay */}
        <div className="flex-1 relative">
          {/* Blurred placeholder cards - vertical list like normal layout */}
          <div className="blur-[4px] pointer-events-none select-none opacity-60">
            <div className="space-y-3">
              {fakePlaceholderMetrics.map((metric, index) => (
                <CompetitorComparisonCard
                  key={index}
                  username={metric.username}
                  followers={metric.followers}
                  postsCount={metric.postsCount}
                  postsPerWeek={metric.postsPerWeek}
                  growth={metric.growth}
                  followersChange={metric.followersChange}
                  isCurrentUser={metric.isCurrentUser}
                  timeRange={timeRange}
                />
              ))}
            </div>
          </div>
          
          {/* Overlay with CTA */}
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="absolute inset-0 bg-background/40" />
            <div className="relative z-20 flex flex-col items-center text-center px-4">
              <div className="w-12 h-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-3">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1.5">
                Dodaj konkurencję
              </h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-[260px]">
                Sprawdź jak wypadasz na tle innych twórców w Twojej branży
              </p>
              <Button
                onClick={() => navigate('/konkurencja')}
                size="sm"
                className="shadow-md"
              >
                Dodaj profile
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Determine which metrics to display based on showAll prop
  let displayedMetrics: ProfileMetrics[];
  
  if (showPlaceholder) {
    displayedMetrics = placeholderMetrics;
  } else if (showAll) {
    // Show current user + all competitors on Competitors page
    displayedMetrics = allMetrics;
  } else {
    // Show user as #1 + top 2 competitors by growth
    const userMetric = allMetrics.find(m => m.isCurrentUser);
    const competitorMetrics = allMetrics.filter(m => !m.isCurrentUser);
    
    // Sort competitors by growth (descending), nulls go to the end
    const sortedCompetitors = [...competitorMetrics].sort((a, b) => {
      if (a.growth === null && b.growth === null) return 0;
      if (a.growth === null) return 1;
      if (b.growth === null) return -1;
      return b.growth - a.growth;
    });
    
    // Take top 2 performers
    const topCompetitors = sortedCompetitors.slice(0, 2);
    
    displayedMetrics = userMetric 
      ? [userMetric, ...topCompetitors]
      : topCompetitors;
  }

  // Show "Add Competitor" CTA only when there's less than 2 competitors
  const competitorCount = displayedMetrics.filter(m => !m.isCurrentUser).length;
  const shouldShowAddCTA = !showAll && competitorCount < 2;

  // Show empty state if no competitors to display
  if (displayedMetrics.length === 0 && showAll) {
    return (
      <div className="glass-card rounded-2xl p-8 md:p-12 h-full flex flex-col items-center justify-center text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
        
        <div className="relative z-10 space-y-4 max-w-md">
          <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-foreground mb-2">
              {t('competitors.noCompetitors')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('competitors.noCompetitorsDesc')}
            </p>
          </div>
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="shadow-md"
          >
            {t('competitors.addCompetitor')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl p-6 md:p-8 h-full flex flex-col relative overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
      
      {/* Header Section */}
      <div className="relative z-10 mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground">
            {t('competitors.performanceComparison')}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground ml-13">
          {showAll ? t('competitors.comparePerformance') : t('competitors.youVsBest')}
        </p>
      </div>
      
      {/* Conditional Layout: Compact for Dashboard, Grid for Competitors Page */}
      {!showAll && displayedMetrics.length <= 3 ? (
        /* Compact Dashboard Layout - List Style */
        <div className="flex-1 relative z-10">
          <div className="space-y-3 relative">
            {/* Blurred placeholder overlay */}
            {showPlaceholder && (
              <div className="absolute inset-0 z-10 flex items-start justify-center pt-16 rounded-xl">
                <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px] rounded-xl" />
                <div className="relative z-20 flex flex-col items-center gap-3 text-center px-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                  {hasNoCompetitors ? (
                    <>
                      <p className="text-base font-semibold text-foreground">
                        Dodaj konkurencję
                      </p>
                      <p className="text-sm text-muted-foreground max-w-[250px]">
                        Sprawdź jak wypadasz na tle innych twórców
                      </p>
                      <Button
                        onClick={() => navigate('/konkurencja')}
                        size="sm"
                        className="mt-1"
                      >
                        Uzupełnij
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-base font-semibold text-foreground">
                        Zbieramy dane
                      </p>
                      <p className="text-sm text-muted-foreground max-w-[250px]">
                        Analizujemy Twój profil i konkurencję
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {displayedMetrics.map((metric, index) => (
              <div
                key={`${metric.username}-${index}`}
                className={cn(
                  "animate-scale-in",
                  showPlaceholder && "blur-[3px] pointer-events-none select-none"
                )}
                style={{ animationDelay: `${index * 0.15}s`, animationFillMode: 'backwards' }}
              >
                <CompetitorComparisonCard
                  username={metric.username}
                  followers={metric.followers}
                  postsCount={metric.postsCount}
                  postsPerWeek={metric.postsPerWeek}
                  growth={metric.growth}
                  followersChange={metric.followersChange}
                  isCurrentUser={metric.isCurrentUser}
                  timeRange={timeRange}
                />
              </div>
            ))}
          </div>
          
          {/* Add Competitor Button - centered below list */}
          {displayedMetrics.length < 3 && !showPlaceholder && (
            <div className="mt-4 flex justify-center">
              <Button
                onClick={() => navigate('/konkurencja')}
                variant="outline"
                className="group"
              >
                {t('competitors.addCompetitor')}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          )}
        </div>
      ) : (
        /* Grid Layout for Full View */
        <>
          <div className={cn(
            "grid gap-4 flex-1 relative z-10",
            showAll ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
          )}>
            {displayedMetrics.map((metric, index) => (
              <div
                key={`${metric.username}-${index}`}
                className="animate-scale-in"
                style={{ animationDelay: `${index * 0.15}s`, animationFillMode: 'backwards' }}
              >
              <CompetitorComparisonCard
                  username={metric.username}
                  followers={metric.followers}
                  postsCount={metric.postsCount}
                  postsPerWeek={metric.postsPerWeek}
                  growth={metric.growth}
                  followersChange={metric.followersChange}
                  isCurrentUser={metric.isCurrentUser}
                  timeRange={timeRange}
                />
              </div>
            ))}
            
            {/* Add Competitor CTA Card */}
            {shouldShowAddCTA && (
              <div 
                className="animate-scale-in"
                style={{ animationDelay: `${displayedMetrics.length * 0.1}s` }}
              >
                <div 
                  className="h-full glass-card rounded-xl p-6 border-2 border-dashed border-primary/30 hover:border-primary/50 hover:bg-primary/5 transition-all flex flex-col items-center justify-center text-center cursor-pointer group"
                  onClick={() => navigate('/konkurencja')}
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center mb-3 transition-colors">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">
                    {t('competitors.addCompetitor')}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t('competitors.trackPerformance')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Show More Button - only on Dashboard */}
          {!showAll && competitors.filter(c => c.is_active).length > 2 && (
            <div className="mt-6 flex justify-center relative z-10">
              <Button
                onClick={() => navigate('/konkurencja')}
                variant="outline"
                className="group shadow-sm hover:shadow-md transition-all"
              >
                {t('competitors.showMore')}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          )}
        </>
      )}

    </div>
  );
};
